import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Palette,
  Type,
  Eye,
  Loader2,
  ArrowLeft,
  ArrowRight,
  RotateCcw,
  Sparkles,
} from 'lucide-react'
import { Button, Input, Label, Card, CardHeader, CardTitle } from '@/components/ui'
import { Skeleton } from '@/components/ui/skeleton'
import { useOnboardingSubmission } from '@/hooks/onboarding'
import { useUpdateSubmission, useCreatePreview } from '@/hooks/onboarding'

const FONT_OPTIONS = [
  'Inter',
  'Playfair Display',
  'Roboto',
  'Montserrat',
  'Lora',
  'Oswald',
  'Raleway',
  'Poppins',
]

const COLOR_PRESETS = [
  { name: 'Classic Dark', primary: '#1a1a2e', secondary: '#e94560' },
  { name: 'Ocean Blue', primary: '#0a2647', secondary: '#2c74b3' },
  { name: 'Forest Green', primary: '#1b4332', secondary: '#52b788' },
  { name: 'Royal Purple', primary: '#2d1b69', secondary: '#8b5cf6' },
  { name: 'Warm Brown', primary: '#3c2415', secondary: '#d97706' },
  { name: 'Midnight Gold', primary: '#111827', secondary: '#f59e0b' },
  { name: 'Slate Rose', primary: '#334155', secondary: '#f43f5e' },
  { name: 'Charcoal Teal', primary: '#1e293b', secondary: '#14b8a6' },
]

export default function OnboardingStaging() {
  const { submissionId } = useParams<{ submissionId: string }>()
  const navigate = useNavigate()
  const id = Number(submissionId)

  const { data: submission, isLoading, error } = useOnboardingSubmission(id)
  const updateSubmission = useUpdateSubmission(id)
  const createPreview = useCreatePreview(id)

  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewReady, setPreviewReady] = useState(false)

  // Local theme state for real-time preview
  const [theme, setTheme] = useState({
    primary_color: '#1a1a2e',
    secondary_color: '#e94560',
    font_family: 'Inter',
  })

  // Initialize theme from submission
  useEffect(() => {
    if (submission) {
      setTheme({
        primary_color: submission.primary_color,
        secondary_color: submission.secondary_color,
        font_family: submission.font_family,
      })
    }
  }, [submission])

  // Send theme updates to iframe via postMessage
  const sendThemeToIframe = useCallback(
    (updates: Partial<typeof theme>) => {
      const newTheme = { ...theme, ...updates }
      setTheme(newTheme)

      if (iframeRef.current?.contentWindow) {
        iframeRef.current.contentWindow.postMessage(
          { type: 'theme-update', payload: newTheme },
          '*'
        )
      }
    },
    [theme]
  )

  // Provision preview tenant
  const handleCreatePreview = async () => {
    setPreviewLoading(true)
    try {
      const result = await createPreview.mutateAsync()
      setPreviewUrl(result.preview_url)
    } finally {
      setPreviewLoading(false)
    }
  }

  // Save theme back to submission
  const handleSaveTheme = () => {
    updateSubmission.mutate({
      primary_color: theme.primary_color,
      secondary_color: theme.secondary_color,
      font_family: theme.font_family,
    })
  }

  // Reset to defaults
  const handleReset = () => {
    const defaults = {
      primary_color: '#1a1a2e',
      secondary_color: '#e94560',
      font_family: 'Inter',
    }
    sendThemeToIframe(defaults)
  }

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Skeleton className="h-[80vh] w-full max-w-7xl" />
      </div>
    )
  }

  if (error || !submission) {
    return (
      <div className="container mx-auto max-w-2xl p-6">
        <Card className="border-destructive/40 bg-destructive/5">
          <CardHeader>
            <CardTitle className="text-destructive">Submission not found</CardTitle>
          </CardHeader>
        </Card>
      </div>
    )
  }

  return (
    <div className="flex h-[calc(100vh-64px)]">
      {/* Left Panel — Theme Controls */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className="flex w-80 flex-col overflow-y-auto border-r bg-card"
      >
        <div className="border-b p-4">
          <div className="mb-1 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <h2 className="font-semibold">Staging Area</h2>
          </div>
          <p className="text-xs text-muted-foreground">{submission.shop_name}</p>
        </div>

        {/* Preview Button */}
        {!previewUrl && (
          <div className="border-b p-4">
            <Button className="w-full" onClick={handleCreatePreview} disabled={previewLoading}>
              {previewLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating Preview...
                </>
              ) : (
                <>
                  <Eye className="mr-2 h-4 w-4" /> Launch Preview
                </>
              )}
            </Button>
            <p className="mt-2 text-center text-xs text-muted-foreground">
              Creates a live preview of your barbershop
            </p>
          </div>
        )}

        {/* Color Presets */}
        <div className="border-b p-4">
          <h3 className="mb-3 flex items-center gap-2 text-sm font-medium">
            <Palette className="h-4 w-4" /> Color Presets
          </h3>
          <div className="grid grid-cols-2 gap-2">
            {COLOR_PRESETS.map((preset) => (
              <button
                key={preset.name}
                onClick={() =>
                  sendThemeToIframe({
                    primary_color: preset.primary,
                    secondary_color: preset.secondary,
                  })
                }
                className="flex items-center gap-2 rounded-lg border p-2 text-left transition-colors hover:border-primary/50"
              >
                <div className="flex shrink-0 gap-1">
                  <div className="h-4 w-4 rounded-sm" style={{ backgroundColor: preset.primary }} />
                  <div
                    className="h-4 w-4 rounded-sm"
                    style={{ backgroundColor: preset.secondary }}
                  />
                </div>
                <span className="truncate text-xs">{preset.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Custom Colors */}
        <div className="space-y-4 border-b p-4">
          <h3 className="flex items-center gap-2 text-sm font-medium">
            <Palette className="h-4 w-4" /> Custom Colors
          </h3>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Primary Color</Label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={theme.primary_color}
                  onChange={(e) => sendThemeToIframe({ primary_color: e.target.value })}
                  className="h-9 w-10 cursor-pointer rounded border"
                />
                <Input
                  value={theme.primary_color}
                  onChange={(e) => sendThemeToIframe({ primary_color: e.target.value })}
                  className="h-9 font-mono text-xs"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Accent Color</Label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={theme.secondary_color}
                  onChange={(e) => sendThemeToIframe({ secondary_color: e.target.value })}
                  className="h-9 w-10 cursor-pointer rounded border"
                />
                <Input
                  value={theme.secondary_color}
                  onChange={(e) => sendThemeToIframe({ secondary_color: e.target.value })}
                  className="h-9 font-mono text-xs"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Font */}
        <div className="border-b p-4">
          <h3 className="mb-3 flex items-center gap-2 text-sm font-medium">
            <Type className="h-4 w-4" /> Font
          </h3>
          <div className="space-y-1">
            {FONT_OPTIONS.map((font) => (
              <button
                key={font}
                onClick={() => sendThemeToIframe({ font_family: font })}
                className={`w-full rounded px-3 py-2 text-left text-sm transition-colors ${
                  theme.font_family === font
                    ? 'bg-primary/10 font-medium text-primary'
                    : 'hover:bg-muted'
                }`}
                style={{ fontFamily: font }}
              >
                {font}
              </button>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="mt-auto space-y-2 border-t p-4">
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleReset} className="flex-1">
              <RotateCcw className="mr-1 h-3 w-3" /> Reset
            </Button>
            <Button
              size="sm"
              onClick={handleSaveTheme}
              disabled={updateSubmission.isPending}
              className="flex-1"
            >
              {updateSubmission.isPending ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                'Save Theme'
              )}
            </Button>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/onboarding/${id}`)}
              className="flex-1"
            >
              <ArrowLeft className="mr-1 h-3 w-3" /> Back
            </Button>
            <Button size="sm" onClick={() => navigate(`/onboarding/${id}`)} className="flex-1">
              Continue <ArrowRight className="ml-1 h-3 w-3" />
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Right Panel — Preview Iframe */}
      <div className="relative flex-1 bg-muted/30">
        {!previewUrl ? (
          <div className="flex h-full flex-col items-center justify-center p-8 text-center">
            <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-2xl bg-primary/10">
              <Eye className="h-10 w-10 text-primary" />
            </div>
            <h3 className="mb-2 text-xl font-semibold">Preview Your Barbershop</h3>
            <p className="mb-6 max-w-md text-muted-foreground">
              Click "Launch Preview" to create a live preview of your barbershop. Then use the
              controls on the left to customize colors and fonts in real-time.
            </p>
            <Button onClick={handleCreatePreview} disabled={previewLoading}>
              {previewLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating...
                </>
              ) : (
                <>
                  <Eye className="mr-2 h-4 w-4" /> Launch Preview
                </>
              )}
            </Button>
          </div>
        ) : (
          <>
            {!previewReady && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/80">
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">Loading preview...</p>
                </div>
              </div>
            )}
            <iframe
              ref={iframeRef}
              src={previewUrl}
              className="h-full w-full border-0"
              onLoad={() => {
                setPreviewReady(true)
                // Send current theme to iframe once loaded
                if (iframeRef.current?.contentWindow) {
                  iframeRef.current.contentWindow.postMessage(
                    { type: 'theme-update', payload: theme },
                    '*'
                  )
                }
              }}
              title="Barbershop Preview"
            />
          </>
        )}
      </div>
    </div>
  )
}
